import { RefObject } from 'react';

import { useHotkeys } from 'react-hotkeys-hook';

import { hotkeyFor } from './ribbonShortcutRegistry';
import { RIBBON_SCOPE } from './useRibbonHotkeysScope';

/**
 * All ribbon shortcuts share one scope, disabled whenever a modal or
 * side/review panel is open (see `useRibbonHotkeysScope`). Bare letters and
 * `shift+letter` only - never intercepted by a browser, so `preventDefault`
 * is unnecessary (proposal §6).
 */
const ribbonHotkeyOptions = { scopes: [RIBBON_SCOPE] };

/**
 * Measure section (§7): Ruler, System Correction, Insulation Painting, Pipe
 * Snipping, Insulation Polygon. `Esc` (Clear Selection & Stop Tool) is
 * intentionally excluded - it's already handled by the legacy
 * `useKeyEsc`/`DomEventExt` listeners (§2.1), which are out of scope for
 * this phase (§4). `Note & Sticker` (N) is also excluded - the button is
 * currently hidden with no visual feedback to key off of, so the binding
 * stays reserved rather than wired to a no-op-looking toggle (§7 calls this
 * out as "activate when the button returns").
 */
export function useMeasureSectionShortcuts(opts: {
  /** mirrors `MapControlSection`'s local `toggleRuler` */
  onToggleRuler: () => void;
  onMagicBrushOpenHandler: () => void;
  onInsulationPaintingHandler: () => void;
  onScissorOpenHandler: () => void;
  onPolygonOpenHandler: () => void;
  /** flag-gated (`VITE_APP_ENABLE_INSULATION`); button doesn't render when false */
  polygonAvailable: boolean;
}) {
  const {
    onToggleRuler,
    onMagicBrushOpenHandler,
    onInsulationPaintingHandler,
    onScissorOpenHandler,
    onPolygonOpenHandler,
    polygonAvailable,
  } = opts;

  // R - toggle Ruler tool
  useHotkeys(hotkeyFor('ruler'), onToggleRuler, ribbonHotkeyOptions, [
    onToggleRuler,
  ]);

  // B - open System Correction directly
  useHotkeys(
    hotkeyFor('magicBrush'),
    onMagicBrushOpenHandler,
    ribbonHotkeyOptions,
    [onMagicBrushOpenHandler],
  );

  // W - open Insulation Painting directly
  useHotkeys(
    hotkeyFor('insulationPainting'),
    onInsulationPaintingHandler,
    ribbonHotkeyOptions,
    [onInsulationPaintingHandler],
  );

  // C - Pipe Snipping Tool ("Cut" mnemonic)
  useHotkeys(hotkeyFor('pipeSnip'), onScissorOpenHandler, ribbonHotkeyOptions, [
    onScissorOpenHandler,
  ]);

  // G - Insulation Polygon Tool
  useHotkeys(
    hotkeyFor('insulationPolygon'),
    onPolygonOpenHandler,
    { ...ribbonHotkeyOptions, enabled: polygonAvailable },
    [onPolygonOpenHandler, polygonAvailable],
  );
}

/**
 * Filters section (§7): symbol/system filter popups, Lock Empty, Inverse
 * Filtering. The Shape dropdown has no shortcut (plain value-select).
 */
export function useFilterSectionShortcuts(opts: {
  openSymbolFilterMenu: () => void;
  openSystemFilterMenu: () => void;
  lockEmptySystemToggle: () => void;
  inverseFilterToggle: () => void;
}) {
  const {
    openSymbolFilterMenu,
    openSystemFilterMenu,
    lockEmptySystemToggle,
    inverseFilterToggle,
  } = opts;

  // F - symbol type/group filter popup
  useHotkeys(
    hotkeyFor('symbolFilter'),
    openSymbolFilterMenu,
    ribbonHotkeyOptions,
    [openSymbolFilterMenu],
  );

  // Shift+F - system filter popup
  useHotkeys(
    hotkeyFor('systemFilter'),
    openSystemFilterMenu,
    ribbonHotkeyOptions,
    [openSystemFilterMenu],
  );

  // L - Lock Empty (symbol), persistent toggle
  useHotkeys(
    hotkeyFor('lockEmptySymbol'),
    lockEmptySystemToggle,
    ribbonHotkeyOptions,
    [lockEmptySystemToggle],
  );

  // I - Inverse Filtering, persistent toggle
  useHotkeys(
    hotkeyFor('inverseFiltering'),
    inverseFilterToggle,
    ribbonHotkeyOptions,
    [inverseFilterToggle],
  );
}

/**
 * Size section (§7): Size Merge Stamp, Lock Empty (size). The size
 * dropdown has no shortcut (plain value-select).
 */
export function useSizeSectionShortcuts(opts: {
  stampEnableHandler: (size: string) => void;
  selectedSizeType: string;
  lockEmptySizeToggle: () => void;
}) {
  const { stampEnableHandler, selectedSizeType, lockEmptySizeToggle } = opts;
  // mirrors the Size Merge Stamp button's own `disabled` condition
  const stampAvailable =
    selectedSizeType !== 'all' && selectedSizeType !== 'empty';

  // S - Size Merge Stamp, using whichever size is currently selected
  useHotkeys(
    hotkeyFor('sizeMergeStamp'),
    () => stampEnableHandler(selectedSizeType),
    { ...ribbonHotkeyOptions, enabled: stampAvailable },
    [stampEnableHandler, selectedSizeType, stampAvailable],
  );

  // Shift+L - Lock Empty (size), persistent toggle
  useHotkeys(
    hotkeyFor('lockEmptySize'),
    lockEmptySizeToggle,
    ribbonHotkeyOptions,
    [lockEmptySizeToggle],
  );
}

/**
 * Add a new Fitting section (§7): Duct/Pipe/Plumbing popovers, their
 * length variants, and Add New Equipment.
 */
export function useNewElementSectionShortcuts(opts: {
  /** popover trigger buttons - clicked, not toggled, so the same fitting
   * picker opens as with a mouse (see `fitting-menu-popover.tsx`) */
  ductButtonRef: RefObject<HTMLButtonElement>;
  pipeButtonRef: RefObject<HTMLButtonElement>;
  plumButtonRef: RefObject<HTMLButtonElement>;
  /** exact same calls `LengthSelect`'s `clickHandler` makes */
  selectDuctLength: () => void;
  selectPipeLength: () => void;
  /** exact same calls the Equipment button's `onClick` makes */
  selectEquipment: () => void;
}) {
  const {
    ductButtonRef,
    pipeButtonRef,
    plumButtonRef,
    selectDuctLength,
    selectPipeLength,
    selectEquipment,
  } = opts;

  // D - Duct popover
  useHotkeys(
    hotkeyFor('duct'),
    () => ductButtonRef.current?.click(),
    ribbonHotkeyOptions,
    [ductButtonRef],
  );

  // P - Pipe popover
  useHotkeys(
    hotkeyFor('pipe'),
    () => pipeButtonRef.current?.click(),
    ribbonHotkeyOptions,
    [pipeButtonRef],
  );

  // U - Plumbing popover ("plUmbing" - P/L both taken/awkward)
  useHotkeys(
    hotkeyFor('plumbing'),
    () => plumButtonRef.current?.click(),
    ribbonHotkeyOptions,
    [plumButtonRef],
  );

  // Shift+D - Duct Length, paired with Duct
  useHotkeys(hotkeyFor('ductLength'), selectDuctLength, ribbonHotkeyOptions, [
    selectDuctLength,
  ]);

  // Shift+P - Pipe Length, paired with Pipe
  useHotkeys(hotkeyFor('pipeLength'), selectPipeLength, ribbonHotkeyOptions, [
    selectPipeLength,
  ]);

  // E - Add New Equipment
  useHotkeys(hotkeyFor('equipment'), selectEquipment, ribbonHotkeyOptions, [
    selectEquipment,
  ]);
}

/** Panel buttons (§7): Takeoff Summary, Review Fittings. */
export function usePanelButtonShortcuts(opts: {
  popupSummaryView: () => void;
  slideTotalCorrectionHandler: () => void;
}) {
  const { popupSummaryView, slideTotalCorrectionHandler } = opts;

  // T - Takeoff Summary
  useHotkeys(
    hotkeyFor('takeoffSummary'),
    popupSummaryView,
    ribbonHotkeyOptions,
    [popupSummaryView],
  );

  // V - Review Fittings ("R"/"W" both avoided - Ruler / free)
  useHotkeys(
    hotkeyFor('reviewFittings'),
    slideTotalCorrectionHandler,
    ribbonHotkeyOptions,
    [slideTotalCorrectionHandler],
  );
}

/**
 * Meta (§7, §8 Phase 3): `Shift+/` (`?` on a US layout) opens the shortcuts
 * cheatsheet - `react-hotkeys-hook` binds by physical combo, so this stays
 * portable across layouts. Same `RIBBON_SCOPE` as everything else, so it's
 * suppressed while another modal/panel is already open (no stacking).
 *
 * Bound as `shift+slash`, not `shift+/`: this library matches against
 * `KeyboardEvent.code` (the physical key, e.g. `"Slash"`), normalized by
 * lowercasing and stripping the `Key`/`Digit`/`Numpad` prefix - never
 * against the character the key produces. Letters survive that unchanged
 * (`KeyR` -> `r`), but the `/` key's code is `Slash` -> `slash`, so the
 * literal `/` string never matches a real keypress.
 */
export function useCheatsheetShortcut(openCheatsheet: () => void) {
  useHotkeys('shift+slash', openCheatsheet, ribbonHotkeyOptions, [
    openCheatsheet,
  ]);
}

/**
 * Renderless wrapper around `useCheatsheetShortcut`, so it can be mounted
 * directly in JSX *inside* `HotkeysProvider`. Calling the hook straight from
 * `FileDetailsView`'s body doesn't work: that component creates the
 * `HotkeysProvider` as part of its own return value, which makes the
 * provider a *child* of `FileDetailsView` in the tree, not an ancestor - so
 * `useHotkeysContext()` inside the hook would see the context's default
 * `activeScopes: []` instead of `['ribbon']`, and the `scopes: ['ribbon']`
 * option on the hotkey would make it bail before ever attaching a listener
 * (same reasoning as `RibbonHotkeysScopeSync` in `useRibbonHotkeysScope.ts`).
 */
export const CheatsheetHotkey = ({
  openCheatsheet,
}: {
  openCheatsheet: () => void;
}) => {
  useCheatsheetShortcut(openCheatsheet);
  return null;
};
